import React, { useEffect, useState } from "react";

import { changeInsurance } from "../../slices/actions";
import { toggleModal } from "../../slices/popupSlice";
import {
	insuranceAdded,
	insuranceRemoved,
	prepareInsuranceItem,
	trackPopupViewEvent,
} from "../../tracking/googleAnalytics";
import { useDispatch, useSelector } from "../../utils/hooks";
import strings from "../../utils/strings";

import { INSURANCE_MODE, INSURANCE_TYPE } from "./Insurance.type";
import { InsuranceContent } from "./InsuranceContent";
import "./Insurance.scss";

export const Insurance = () => {
	const dispatch = useDispatch();

	const cartId = useSelector(
		(state) => state.popup.products.mainProduct.cartId,
	);

	const cartItemId = useSelector(
		(state) => state.popup.products.mainProduct.cartItemId,
	);

	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	const selectedInsuranceId = useSelector(
		(state) => state.popup.products.mainProduct.selectedInsuranceId,
	);

	const redirectUrl = useSelector(
		(state) => state.popup.modal?.config?.redirectUrl,
	);

	const [currentInsuranceId, setCurrentInsuranceId] = useState(
		selectedInsuranceId || insurance[0].id,
	);

	const chooseInsurance = ({ event, insurance, mode }) => {
		let id = null;
		if (mode === INSURANCE_MODE.REMOVE) {
			event.preventDefault();
			(
				document.getElementById(currentInsuranceId) as HTMLInputElement
			).checked = false;
		} else {
			id = insurance.id;
		}

		setCurrentInsuranceId(id);

		return dispatch(
			changeInsurance({
				cartId,
				cartItemId,
				insuranceId: id,
			}),
		)
			.unwrap()
			.then((response) => response);
	};

	const handleChange = async (event, mode) => {
		event.preventDefault();
		const insuranceLine = insurance.find(
			(insuranceLine) => insuranceLine.id === currentInsuranceId,
		);

		const response = await chooseInsurance({
			event,
			insurance: insuranceLine,
			mode,
		});

		const insuranceItem = prepareInsuranceItem({
			id: insuranceLine.id,
			name: insuranceLine.description,
			price: insuranceLine.price,
			variant: INSURANCE_TYPE.REGULAR,
		});

		if (mode === INSURANCE_MODE.CHANGE) {
			insuranceAdded(insuranceItem);
		} else if (selectedInsuranceId) {
			insuranceRemoved(insuranceItem);
		}

		trackPopupViewEvent({
			category: "Information",
			name:
				mode === INSURANCE_MODE.CHANGE
					? "Insurance modal closed (insurance set)"
					: "Insurance modal closed (insurance removed)",
		});

		if (response && redirectUrl) {
			window.location.href = redirectUrl;
		}

		dispatch(toggleModal({ id: "", isOpen: false, config: null }));
	};

	if (!insurance || insurance.length === 0) {
		return null;
	}

	useEffect(() => {
		trackPopupViewEvent({
			category: "Information",
			name: "Standard insurance modal shown",
		});
	}, []);

	return (
		<section className="addtocart-insurance__container">
			<form
				className="addtocart-insurance"
				aria-labelledby="insurance__heading"
			>
				<div className="addtocart-insurance__info">
					<h3>{strings["addtocartpopup.insurance.title"]}</h3>
					<h2
						className="addtocart-insurance__info--title"
						id="insurance__heading"
						dangerouslySetInnerHTML={{
							__html: strings["addtocartpopup.insurance.subtitle"],
						}}
					/>
					<InsuranceContent />
				</div>

				<div className="addtocart-insurance__inputs">
					{insurance?.map((insuranceLine) => (
						<div
							className="addtocart-insurance__input-wrapper"
							key={insuranceLine.id}
						>
							<input
								type="radio"
								id={insuranceLine.id}
								data-testid={`insurance-option-input-${insuranceLine.id}`}
								checked={insuranceLine.id === currentInsuranceId}
								name="insurance"
								value={insuranceLine.description}
								onChange={(event) =>
									chooseInsurance({
										event,
										insurance: insuranceLine,
										mode: INSURANCE_MODE.CHANGE,
									})
								}
							/>
							<label
								htmlFor={insuranceLine.id}
								data-testid={`insurance-option-label-${insuranceLine.id}`}
								className="addtocart-input__label"
							>
								{insuranceLine.description}
								<span className="addtocart-input__label--price">
									{insuranceLine.priceNet}
								</span>
							</label>
						</div>
					))}
				</div>
				<div className="addtocart-insurance__buttons caas">
					<button
						className="addtocart-button btn btn-medium tertiary-grey"
						data-testid="footer-button-continue"
						onClick={(event) => handleChange(event, INSURANCE_MODE.REMOVE)}
					>
						<span>{strings["addtocartpopup.insurance.modal.button.no"]}</span>
					</button>
					<button
						className="addtocart-button btn btn-medium primary"
						data-testid="footer-button-gotocart"
						onClick={(event) => handleChange(event, INSURANCE_MODE.CHANGE)}
					>
						<span>{strings["addtocartpopup.insurance.modal.button.yes"]}</span>
					</button>
				</div>
			</form>
		</section>
	);
};
